import { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useRouter } from 'next/router';
import useMyInfo from '@api/hooks/auth/useMyInfo';
import { getIsLoggedIn } from '@api/selectors/auth';
import useGoalInfo from '@api/hooks/goal/useGoalInfo';
import useTopology from '@api/hooks/topology/useTopology';
import useConstants from '@api/hooks/useConstants';
import { getIsTestTabV2 } from '@api/selectors/constants';
import { getIsPrimeEnabled } from '@api/selectors/topology';
import { useToggleable } from '@stores/toggleables';
import { LOGIN_DRAWER } from '@constants/toggleables';
import useTestTabCards from '@api/hooks/goal/test-tab/useTestTabCards';
import useTestTabRecommended from '@api/hooks/goal/test-tab/useTestTabRecommended';
import usePrimeTrialPromoModal from '@stores/prime-pitch/usePrimeTrialPromoModal';
import { PrimeTrialPromoContextualId } from '@stores/prime-pitch/types';
import useTestTabFilterMetadata from '@api/hooks/goal/test-tab/useTestTabFilterMetadata';
import { getCustomPageDetails } from '@utils/getCustomPageDetails';
import { ALL_TEST_SERIES } from '@constants/segment';
import PrimeBottomBanner from '@cont/PrimeHome/PrimeBottomBanner';
import { BottomSpacer } from '@cont/PrimeHome/PrimeHome.styles';
import TestTabPlusStickyBanner from './Common/TestTabPlusStickyBanner';
import {
  getSubscriptionToggleValue,
  SUBSCRIPTION_TOGGLE
} from './Common/SubscriptionToggle';
import {
  buildTestTabBaseProps,
  saveTestTabAnalyticsContext,
  trackTestTestsSectionClicked,
  trackTestTestsSubSectionClicked
} from './testTab/testTabAnalytics';
import useTestTabEventHandlers from './testTab/useTestTabEventHandlers';
import useTestListingLockState from './testTab/useTestListingLockState';
import useTestTabV2Browse, {
  getListingFilterConfig,
  getTestTabV2ResetFilters
} from './testTab/useTestTabV2Browse';

import TopBar from './Common/TopBar';
import { TEST_SERIES_TAB } from './Common/TestSeriesTabSwitcher';
import Breadcrumbs from './Common/Breadcrumbs';
import TestSeriesListingTestCardsGrid from './Common/TestSeriesListingTestCardsGrid';
import TestTabV2Content from './Common/TestTabV2Content';
import DailyScholarshipTest from './DailyScholarshipTest/DST';
import { getDefaultCategoryTab } from './Common/TestSeriesContentPanel';
import { buildFilterRequestBody } from './testTab/buildFilterRequestBody';
import { getApiTabId } from './testTab/constants';
import { createEmptyFilterSelections } from './testTab/parseFilterMetadata';
import { normalizeRecommended } from './testTab/recommended';

const DEFAULT_LANGUAGE = 1;

const getQueryParam = (value) => (Array.isArray(value) ? value[0] : value);

const TestSeriesListing = ({ goalName: goalNameProp = '' }) => {
  const { query, asPath } = useRouter();
  const goalUID = getQueryParam(query.goalUID);
  const goalSlug = getQueryParam(query.goalSlug);
  const { goalInfo } = useGoalInfo(goalUID);
  const { topology } = useTopology(goalUID);
  const isPrimeEnabled = getIsPrimeEnabled(topology);
  const isLoggedIn = useMyInfo(getIsLoggedIn);
  const isTestTabV2 = useConstants(getIsTestTabV2(isPrimeEnabled));
  const { openElement: showLoginDrawer } = useToggleable(LOGIN_DRAWER);
  const { openPrimeTrialPromoModal } = usePrimeTrialPromoModal();
  const {
    showPrimeStickyBanner,
    testQuotaLeft,
    testQuotaTotal,
    isPlusOrIconicUser,
    isPrimeQuotaExhausted,
    isOfflineCentreLearner
  } = useTestListingLockState(goalUID);

  const goalDisplayName =
    goalNameProp || goalInfo?.name || goalInfo?.title || '';

  const pageDetails = useMemo(
    () =>
      getCustomPageDetails(
        goalDisplayName,
        goalInfo?.title || goalDisplayName,
        ALL_TEST_SERIES
      ),
    [goalDisplayName, goalInfo?.title]
  );
  const [activeTestTab, setActiveTestTab] = useState(TEST_SERIES_TAB.ALL);
  const [activeCategoryTab, setActiveCategoryTab] = useState(() =>
    getDefaultCategoryTab(TEST_SERIES_TAB.ALL)
  );
  const getResetFilters = useCallback(
    () =>
      isTestTabV2
        ? getTestTabV2ResetFilters(isPlusOrIconicUser)
        : createEmptyFilterSelections(),
    [isPlusOrIconicUser, isTestTabV2]
  );
  const [appliedFilters, setAppliedFilters] = useState(getResetFilters);
  const isPlusTabSelected =
    getSubscriptionToggleValue(appliedFilters.subscription) ===
    SUBSCRIPTION_TOGGLE.PLUS_ICONIC;

  useEffect(() => {
    if (!isTestTabV2) {
      return;
    }

    setAppliedFilters((prev) => ({
      ...prev,
      subscription: getTestTabV2ResetFilters(isPlusOrIconicUser).subscription
    }));
  }, [isPlusOrIconicUser, isTestTabV2]);

  const {
    selectedBrowseSectionId,
    isBrowseLanding,
    browseSections,
    isBrowseLoading,
    resetBrowseSelection,
    handleBrowseSectionSelect,
    handleBrowseClose
  } = useTestTabV2Browse({
    goalUid: goalUID,
    goalDisplayName,
    language: DEFAULT_LANGUAGE,
    subscription: appliedFilters.subscription,
    isPlusOrIconicUser,
    enabled: isTestTabV2,
    activeTestTab,
    setActiveCategoryTab,
    setAppliedFilters
  });

  const { data: recommendedResponse } = useTestTabRecommended({
    goalUid: goalUID,
    languageCode: DEFAULT_LANGUAGE,
    enabled: isTestTabV2 && isLoggedIn
  });
  const recommended = useMemo(
    () =>
      normalizeRecommended(recommendedResponse, goalDisplayName, {
        isPlusOrIconicUser,
        isPrimeQuotaExhausted,
        isPlusTabSelected
      }),
    [
      goalDisplayName,
      isPlusOrIconicUser,
      isPlusTabSelected,
      isPrimeQuotaExhausted,
      recommendedResponse
    ]
  );

  const apiTabId = isBrowseLanding
    ? null
    : getApiTabId(activeTestTab, activeCategoryTab);

  const { filterConfig } = useTestTabFilterMetadata({
    goalUid: goalUID,
    apiTabId,
    language: DEFAULT_LANGUAGE,
    view: activeTestTab,
    categoryTabId: activeCategoryTab,
    isLoggedIn
  });
  const listingFilterConfig = getListingFilterConfig(filterConfig, isTestTabV2);

  const filterBody = useMemo(() => {
    if (!apiTabId) {
      return null;
    }

    return buildFilterRequestBody({
      apiTabId,
      categoryTabId: activeCategoryTab,
      appliedSelections: appliedFilters,
      isLoggedIn
    });
  }, [activeCategoryTab, apiTabId, appliedFilters, isLoggedIn]);

  const {
    cards,
    isInitialLoading,
    isRefreshing,
    isError,
    hasMore,
    loadMore,
    isValidating
  } = useTestTabCards({
    goalUid: goalUID,
    view: activeTestTab,
    categoryTabId: activeCategoryTab,
    filterBody,
    language: DEFAULT_LANGUAGE,
    isLoggedIn
  });

  const {
    handleSubscriptionToggle,
    handleRandomTestToggle,
    handleRecommendedUpsell,
    handlePlusStickyCheckout,
    handleTestCardClick
  } = useTestTabEventHandlers({
    asPath,
    goalUID,
    goalSlug,
    goalDisplayName,
    isLoggedIn,
    isTestTabV2,
    activeTestTab,
    activeCategoryTab,
    appliedFilters,
    listingFilterConfig,
    isPlusTabSelected,
    openPrimeTrialPromoModal
  });

  const handleTestTabChange = useCallback(
    (nextTab) => {
      if (nextTab === TEST_SERIES_TAB.MY && !isLoggedIn) {
        showLoginDrawer();
        return;
      }

      const nextCategoryTab = getDefaultCategoryTab(nextTab);
      const baseProps = buildTestTabBaseProps({
        view: nextTab,
        categoryTabId: nextCategoryTab,
        appliedSelections: getResetFilters(),
        isTestTabV2
      });

      trackTestTestsSectionClicked({
        goalUid: goalUID,
        goalName: goalDisplayName,
        ...baseProps
      });
      saveTestTabAnalyticsContext(baseProps);

      setActiveTestTab(nextTab);
      setActiveCategoryTab(nextCategoryTab);
      resetBrowseSelection();
      setAppliedFilters(getResetFilters());
    },
    [
      getResetFilters,
      goalDisplayName,
      goalUID,
      isLoggedIn,
      isTestTabV2,
      resetBrowseSelection,
      showLoginDrawer
    ]
  );

  const handleCategoryTabChange = useCallback(
    (nextCategoryTab) => {
      const baseProps = buildTestTabBaseProps({
        view: activeTestTab,
        categoryTabId: nextCategoryTab,
        appliedSelections: getResetFilters(),
        isTestTabV2
      });

      trackTestTestsSubSectionClicked({
        goalUid: goalUID,
        goalName: goalDisplayName,
        ...baseProps
      });
      saveTestTabAnalyticsContext(baseProps);

      setActiveCategoryTab(nextCategoryTab);
      setAppliedFilters(getResetFilters());
    },
    [activeTestTab, getResetFilters, goalDisplayName, goalUID, isTestTabV2]
  );

  const handleAppliedSelectionsChange = useCallback(
    (nextSelections) => {
      saveTestTabAnalyticsContext(
        buildTestTabBaseProps({
          view: activeTestTab,
          categoryTabId: activeCategoryTab,
          appliedSelections: nextSelections,
          isTestTabV2
        })
      );
      setAppliedFilters(nextSelections);
    },
    [activeCategoryTab, activeTestTab, isTestTabV2]
  );

  const showPrimeSticky = isTestTabV2
    ? showPrimeStickyBanner && !isPlusTabSelected
    : showPrimeStickyBanner;
  const showPlusSticky =
    isTestTabV2 &&
    isLoggedIn &&
    isPrimeEnabled &&
    !isPlusOrIconicUser &&
    !isOfflineCentreLearner &&
    isPlusTabSelected;
  const isBrowsePinned =
    isTestTabV2 &&
    activeTestTab === TEST_SERIES_TAB.ALL &&
    Boolean(selectedBrowseSectionId);

  return (
    <>
      {!isBrowsePinned && (
        <TopBar
          breadcrumbs={
            <Breadcrumbs
              type={Breadcrumbs.TYPE.ONLY_TEST_SERIES}
              goalDisplayName={goalDisplayName}
            />
          }
          afterBreadcrumbs={
            <DailyScholarshipTest
              goalUID={goalUID}
              goalSlug={goalSlug}
              goalName={goalDisplayName}
            />
          }
          title={pageDetails?.title}
          description={goalDisplayName ? pageDetails?.description : undefined}
          activeTab={activeTestTab}
          onTabChange={handleTestTabChange}
          activeCategoryTab={activeCategoryTab}
          onCategoryTabChange={handleCategoryTabChange}
          filterConfig={listingFilterConfig}
          appliedSelections={appliedFilters}
          onAppliedSelectionsChange={handleAppliedSelectionsChange}
          showContentPanel={
            !isTestTabV2 || activeTestTab === TEST_SERIES_TAB.MY
          }
        />
      )}
      {isTestTabV2 ? (
        <TestTabV2Content
          activeTestTab={activeTestTab}
          activeCategoryTab={activeCategoryTab}
          appliedFilters={appliedFilters}
          recommended={recommended}
          onRecommendedUpsell={handleRecommendedUpsell}
          onRandomTestToggle={handleRandomTestToggle}
          onSubscriptionToggle={handleSubscriptionToggle}
          showSubscriptionToggle={
            !isPlusOrIconicUser && activeTestTab === TEST_SERIES_TAB.ALL
          }
          browseSections={browseSections}
          isBrowseLoading={isBrowseLoading}
          selectedBrowseSectionId={selectedBrowseSectionId}
          filterConfig={listingFilterConfig}
          onBrowseSelect={handleBrowseSectionSelect}
          onBrowseClose={handleBrowseClose}
          onAppliedSelectionsChange={handleAppliedSelectionsChange}
          cards={cards}
          isInitialLoading={isInitialLoading}
          isRefreshing={isRefreshing}
          isError={isError}
          hasMore={hasMore}
          onLoadMore={loadMore}
          isValidating={isValidating}
          onCardClick={handleTestCardClick}
          hasPitch={showPrimeSticky || showPlusSticky}
        />
      ) : (
        <TestSeriesListingTestCardsGrid
          cards={cards}
          isInitialLoading={isInitialLoading}
          isRefreshing={isRefreshing}
          isError={isError}
          hasMore={hasMore}
          onLoadMore={loadMore}
          isValidating={isValidating}
          onCardClick={handleTestCardClick}
          useFluidColumns={false}
        />
      )}
      {(showPrimeSticky || showPlusSticky) && !isBrowsePinned ? (
        <BottomSpacer style={{ height: 128 }} />
      ) : null}
      {showPrimeSticky ? (
        <PrimeBottomBanner
          title={
            testQuotaLeft <= 0
              ? 'Daily limit reached'
              : `${testQuotaLeft} out of ${testQuotaTotal} free tests left!`
          }
          subtitle="Get unlimited tests with Prime."
          contextualId={PrimeTrialPromoContextualId.TEST}
          goalUID={goalUID}
          goalSlug={goalSlug}
          analyticsPitchSource="Tests Home Sticky Bottomsheet"
        />
      ) : null}
      {showPlusSticky ? (
        <TestTabPlusStickyBanner
          goalSlug={goalSlug}
          goalUID={goalUID}
          onGetPlus={handlePlusStickyCheckout}
        />
      ) : null}
    </>
  );
};

export default TestSeriesListing;

TestSeriesListing.propTypes = {
  goalName: PropTypes.string
};
